/**
 * ZoneCard — карточка зоны главного экрана (ФТ-ЭКРАН-002).
 *
 * compact=true  → узкая вертикальная плашка (для заглушек слева/справа)
 * compact=false → полная карточка со списком проектов
 *
 * enabled=false → заглушка "В разработке" (независимо от compact)
 * enabled=true  → список/блоки проектов
 *
 * Для центральной зоны (accent='center', compact=false) используется
 * блочный вид — карточки с полной информацией (ГИП, даты, стоимость).
 * Старый табличный вид сохранён в комментарии ниже для возможного отката.
 */
import { Link } from 'react-router-dom'
import { Construction, ArrowRight, Calendar, User2, Banknote } from 'lucide-react'
import clsx from 'clsx'
import { useEffectiveRole } from '@/store/auth'
import type { Zone, ZoneProject, ProjectColor } from '@/types'

interface Props {
  title: string
  subtitle?: string
  zone: Zone
  accent: 'left' | 'center' | 'right'
  compact?: boolean
  onCountClick?: () => void
  onSeeAll?: () => void
}

const COLOR_DOT: Record<ProjectColor, string> = {
  red: 'bg-rose-500',
  yellow: 'bg-amber-400',
  green: 'bg-emerald-500',
  grey: 'bg-gray-400',
}

const COLOR_BORDER_L: Record<ProjectColor, string> = {
  red: 'border-l-rose-400',
  yellow: 'border-l-amber-400',
  green: 'border-l-emerald-400',
  grey: 'border-l-gray-300',
}

const ACCENT_BORDER: Record<Props['accent'], string> = {
  left: 'border-gray-200',
  center: 'border-brand-200',
  right: 'border-gray-200',
}

const ACCENT_HEADER: Record<Props['accent'], string> = {
  left: 'bg-gray-50 text-gray-500',
  center: 'bg-brand-50 text-brand-700',
  right: 'bg-gray-50 text-gray-500',
}

export default function ZoneCard({
  title,
  subtitle,
  zone,
  accent,
  compact = false,
  onCountClick,
  onSeeAll,
}: Props) {
  // Compact-режим: узкая вертикальная плашка для неактивных зон
  if (compact && !zone.enabled) {
    return (
      <div
        className={clsx(
          'flex flex-col rounded-xl bg-white border shadow-sm overflow-hidden h-full',
          ACCENT_BORDER[accent],
        )}
      >
        <div className={clsx('px-3 py-3 border-b border-gray-100', ACCENT_HEADER[accent])}>
          <p className="text-xs font-semibold leading-tight">{title}</p>
          <p className="text-lg font-bold tabular-nums mt-1 text-gray-400">{zone.count}</p>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-3 py-6 text-center bg-gray-50/40">
          <Construction className="h-5 w-5 text-gray-300 mb-2" />
          <p className="text-[11px] text-gray-400 font-medium">В разработке</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={clsx(
        'flex flex-col rounded-xl bg-white border shadow-sm overflow-hidden h-full',
        ACCENT_BORDER[accent],
      )}
    >
      <div
        className={clsx(
          'flex items-center justify-between px-4 py-3 border-b border-gray-100',
          ACCENT_HEADER[accent],
        )}
      >
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && <p className="text-xs opacity-70 mt-0.5">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={onCountClick}
          disabled={!onCountClick || !zone.enabled}
          className={clsx(
            'text-2xl font-bold tabular-nums rounded-md px-2.5 py-0.5 transition-colors',
            onCountClick && zone.enabled
              ? 'hover:bg-white/60 cursor-pointer'
              : 'cursor-default',
          )}
          title={zone.enabled ? 'Перейти к диаграмме Ганта' : undefined}
        >
          {zone.count}
        </button>
      </div>

      {!zone.enabled ? (
        <StubBody />
      ) : zone.projects.length === 0 ? (
        <EmptyBody />
      ) : accent === 'center' ? (
        /* ══════ БЛОЧНЫЙ ВИД (центральная зона «В работе») ══════ */
        <>
          <div className="flex-1 overflow-y-auto p-3 grid grid-cols-2 lg:grid-cols-3 gap-2.5 max-h-[32rem] content-start">
            {zone.projects.map((p) => (
              <ProjectBlock key={p.id} project={p} />
            ))}
          </div>
          {onSeeAll && (
            <button
              type="button"
              onClick={onSeeAll}
              className="flex items-center justify-center gap-1.5 border-t border-gray-100 px-4 py-2 text-xs font-medium text-brand-600 hover:bg-gray-50 transition-colors"
            >
              Смотреть все
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </>
      ) : (
        /* ══════ КОМПАКТНЫЙ СПИСОК (боковые зоны) ══════ */
        <>
          <ul className="flex-1 divide-y divide-gray-50 overflow-y-auto max-h-80">
            {zone.projects.slice(0, 8).map((p) => (
              <li key={p.id} className={p.is_mine === false ? 'opacity-60' : undefined}
                title={p.is_mine === false ? 'Чужой проект: только просмотр' : undefined}>
                <Link
                  to={`/projects/${p.id}`}
                  className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                >
                  <span
                    aria-hidden
                    className={clsx(
                      'inline-block h-2.5 w-2.5 rounded-full flex-shrink-0',
                      COLOR_DOT[p.color],
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono text-xs text-gray-500 flex-shrink-0">{p.code}</span>
                      <span className="text-sm font-medium text-gray-900 truncate">{p.name}</span>
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                      {p.chief_name ?? 'ГИП не назначен'}
                      {p.planned_end_date && (
                        <span className="text-gray-400"> · до {formatDate(p.planned_end_date)}</span>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          {onSeeAll && (
            <button
              type="button"
              onClick={onSeeAll}
              className="flex items-center justify-center gap-1.5 border-t border-gray-100 px-4 py-2 text-xs font-medium text-brand-600 hover:bg-gray-50 transition-colors"
            >
              Смотреть все
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </>
      )}

      {/*
       * ══ РЕЗЕРВНЫЙ ТАБЛИЧНЫЙ ВИД (для отката, если блоки не подойдут) ══
       *
       * Чтобы откатиться: замените блок `accent === 'center' ? <> ... </>` выше
       * на содержимое этого комментария (убрав обёртку комментария):
       *
       * <>
       *   <ul className="flex-1 divide-y divide-gray-50 overflow-y-auto max-h-80">
       *     {zone.projects.slice(0, 8).map((p) => (
       *       <li key={p.id}>
       *         <Link
       *           to={`/projects/${p.id}`}
       *           className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 transition-colors"
       *         >
       *           <span className={clsx('inline-block h-2.5 w-2.5 rounded-full flex-shrink-0', COLOR_DOT[p.color])} />
       *           <div className="min-w-0 flex-1">
       *             <div className="flex items-baseline gap-2">
       *               <span className="font-mono text-xs text-gray-500 flex-shrink-0">{p.code}</span>
       *               <span className="text-sm font-medium text-gray-900 truncate">{p.name}</span>
       *             </div>
       *             <div className="text-xs text-gray-500 truncate mt-0.5">
       *               {p.chief_name ?? 'ГИП не назначен'}
       *               {p.planned_end_date && <span className="text-gray-400"> · до {formatDate(p.planned_end_date)}</span>}
       *             </div>
       *           </div>
       *         </Link>
       *       </li>
       *     ))}
       *   </ul>
       *   {onSeeAll && (
       *     <button type="button" onClick={onSeeAll}
       *       className="flex items-center justify-center gap-1.5 border-t border-gray-100 px-4 py-2 text-xs font-medium text-brand-600 hover:bg-gray-50 transition-colors"
       *     >
       *       Смотреть все <ArrowRight className="h-3.5 w-3.5" />
       *     </button>
       *   )}
       * </>
       */}
    </div>
  )
}

// ─── Блок-карточка проекта (центральная зона) ─────────────────────────────────

function ProjectBlock({ project }: { project: ZoneProject }) {
  const role = useEffectiveRole()
  const showCost = role === 'director' || role === 'deputy_director'
  return (
    <Link
      to={`/projects/${project.id}`}
      className={clsx(
        'block rounded-lg border border-gray-100 border-l-4',
        COLOR_BORDER_L[project.color],
        'bg-white shadow-sm hover:shadow-md hover:border-gray-200 transition-all',
        'p-3',
      )}
    >
      {/* Код + название */}
      <div className="flex items-start gap-2 flex-wrap">
        <span className="font-mono text-[11px] text-gray-400 mt-0.5 flex-shrink-0 leading-snug">
          {project.code}
        </span>
        <span className="text-sm font-semibold text-gray-900 leading-snug break-words">
          {project.name}
        </span>
      </div>

      {/* ГИП */}
      {project.chief_name ? (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
          <User2 className="h-3 w-3 flex-shrink-0 text-gray-400" />
          <span className="font-medium text-gray-700">{project.chief_name}</span>
        </div>
      ) : (
        <div className="mt-2 text-xs text-gray-400 italic">ГИП не назначен</div>
      )}

      {/* Даты */}
      {(project.start_date || project.planned_end_date) && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
          <Calendar className="h-3 w-3 flex-shrink-0 text-gray-400" />
          <span>
            {project.start_date ? formatDate(project.start_date) : '-'}
            {' '}-{' '}
            {project.planned_end_date ? formatDate(project.planned_end_date) : '-'}
          </span>
        </div>
      )}

      {/* Стоимость — только директор и замдир */}
      {showCost && project.total_cost && Number(project.total_cost) > 0 && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
          <Banknote className="h-3 w-3 flex-shrink-0 text-gray-400" />
          <span className="font-medium text-gray-700 tabular-nums">
            {Number(project.total_cost).toLocaleString('ru-RU')} ₽
          </span>
        </div>
      )}

      {/* Прогресс */}
      {typeof project.progress === 'number' && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-xs text-gray-400">Готовность</span>
            <span className="text-xs font-medium text-gray-600 tabular-nums">{project.progress}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-gray-100">
            <div
              className={`h-1.5 rounded-full transition-all ${
                project.progress >= 100 ? 'bg-emerald-500' :
                project.progress >= 60  ? 'bg-blue-500' :
                project.progress >= 30  ? 'bg-amber-500' :
                'bg-gray-300'
              }`}
              style={{ width: `${project.progress}%` }}
            />
          </div>
        </div>
      )}
    </Link>
  )
}

// ─── Вспомогательные компоненты ───────────────────────────────────────────────

function StubBody() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 text-center bg-gray-50/50">
      <Construction className="h-8 w-8 text-gray-400 mb-2" />
      <p className="text-xs font-medium text-gray-500">В разработке</p>
      <p className="text-xs text-gray-400 mt-1 max-w-[200px]">
        Раздел появится в одной из следующих версий
      </p>
    </div>
  )
}

function EmptyBody() {
  return (
    <div className="flex-1 flex items-center justify-center px-6 py-10 text-center">
      <p className="text-xs text-gray-400">Проектов в этой зоне нет</p>
    </div>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`
}
